import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getAgencyFiles, getAgencyFileStats, getAgencies, getStaff,
  submitAgencyFileText, uploadAgencyFile, assignAgencyFile, bulkAssignAgencyFiles,
  completeAgencyFile, deleteAgencyFile, downloadFileWithName
} from '../lib/api';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { SearchableSelect } from '../components/ui/searchable-select';
import {
  Funnel, DownloadSimple, UserPlus, CheckCircle, Clock, FileText,
  PaperPlaneTilt, UploadSimple, Trash, CalendarBlank, Warning,
  ChartBar, Files
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { AgencyFileStatsCards } from '../components/agency-files/AgencyFileStatsCards';
import { AgencyFileAssignDialog } from '../components/agency-files/AgencyFileAssignDialog';
import { AgencyFileCompleteDialog } from '../components/agency-files/AgencyFileCompleteDialog';
import { AgencyFileBulkAssignDialog } from '../components/agency-files/AgencyFileBulkAssignDialog';

const OIL_COMPANIES = ['Bharat Gas', 'HPCL', 'Indane'];
const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
  assigned: { label: 'Assigned', color: 'bg-blue-100 text-blue-700', icon: UserPlus },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
};

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

export default function AgencyFilesPage() {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [agencyFilter, setAgencyFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [staffFilter, setStaffFilter] = useState('');

  // Upload form
  const [showUpload, setShowUpload] = useState(false);
  const [uploadMode, setUploadMode] = useState('text');
  const [uploadAgencyId, setUploadAgencyId] = useState('');
  const [uploadOilCompany, setUploadOilCompany] = useState('Bharat Gas');
  const [uploadText, setUploadText] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadNote, setUploadNote] = useState('');
  const [uploadDate, setUploadDate] = useState(new Date().toISOString().split('T')[0]);
  const [uploading, setUploading] = useState(false);

  // Assign dialog
  const [assignDialog, setAssignDialog] = useState({ open: false, file: null });
  const [assignStaffId, setAssignStaffId] = useState('');
  const [assignInstruction, setAssignInstruction] = useState('');

  // Complete dialog
  const [completeDialog, setCompleteDialog] = useState({ open: false, file: null });
  const [completeQty, setCompleteQty] = useState('');
  const [completeSource, setCompleteSource] = useState('cash');
  const [completing, setCompleting] = useState(false);

  // Bulk assign
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkStaffId, setBulkStaffId] = useState('');
  const [bulkInstruction, setBulkInstruction] = useState('');
  const [bulkAssigning, setBulkAssigning] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'owner' || user?.role === 'assistant';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (agencyFilter) params.agency_id = agencyFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (staffFilter) params.staff_id = staffFilter;

      const [filesRes, agenciesRes, staffRes, statsRes] = await Promise.all([
        getAgencyFiles(params),
        getAgencies(),
        isAdmin ? getStaff() : Promise.resolve({ data: [] }),
        isAdmin ? getAgencyFileStats().catch(() => ({ data: null })) : Promise.resolve({ data: null }),
      ]);
      setFiles(filesRes.data);
      setAgencies(agenciesRes.data.filter(a => a.status === 'active'));
      setStaffList(staffRes.data.filter(s => s.role === 'staff'));
      if (statsRes.data) setStats(statsRes.data);
    } catch (err) {
      toast.error('Data load nahi hua');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, agencyFilter, startDate, endDate, staffFilter, isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Upload handlers
  const handleSubmitText = async () => {
    if (!uploadAgencyId) { toast.error('Agency select karo'); return; }
    if (!uploadText.trim()) { toast.error('Text content daalo'); return; }
    setUploading(true);
    try {
      const res = await submitAgencyFileText({
        agency_id: uploadAgencyId, oil_company: uploadOilCompany,
        text_content: uploadText, note: uploadNote, entry_date: uploadDate,
      });
      toast.success(res.data.message);
      resetUploadForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submit failed');
    } finally {
      setUploading(false);
    }
  };

  const handleUploadFile = async () => {
    if (!uploadAgencyId) { toast.error('Agency select karo'); return; }
    if (!uploadFile) { toast.error('File select karo'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('agency_id', uploadAgencyId);
      fd.append('oil_company', uploadOilCompany);
      fd.append('note', uploadNote);
      fd.append('entry_date', uploadDate);
      const res = await uploadAgencyFile(fd);
      toast.success(res.data.message);
      resetUploadForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadText(''); setUploadFile(null); setUploadNote('');
    setUploadAgencyId(''); setShowUpload(false);
  };

  // Assign handler
  const handleAssign = async () => {
    if (!assignStaffId) { toast.error('Staff select karo'); return; }
    try {
      await assignAgencyFile(assignDialog.file.id, { staff_id: assignStaffId, instruction: assignInstruction });
      toast.success('File assigned!');
      setAssignDialog({ open: false, file: null });
      setAssignStaffId(''); setAssignInstruction('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Assign failed');
    }
  };

  // Complete handler
  const handleComplete = async () => {
    const qty = parseInt(completeQty);
    if (!qty || qty <= 0) { toast.error('Quantity daalo (> 0)'); return; }
    setCompleting(true);
    try {
      const res = await completeAgencyFile(completeDialog.file.id, { quantity: qty, source: completeSource });
      toast.success(res.data.message);
      setCompleteDialog({ open: false, file: null });
      setCompleteQty(''); setCompleteSource('cash');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Complete failed');
    } finally {
      setCompleting(false);
    }
  };

  // Delete handler
  const handleDelete = async (fileId) => {
    if (!window.confirm('Delete this agency file?')) return;
    try {
      await deleteAgencyFile(fileId);
      toast.success('File deleted');
      setSelectedIds(prev => { const n = new Set(prev); n.delete(fileId); return n; });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  // Bulk assign handler
  const handleBulkAssign = async () => {
    if (!bulkStaffId) { toast.error('Staff select karo'); return; }
    if (selectedIds.size === 0) { toast.error('Koi file select nahi ki'); return; }
    setBulkAssigning(true);
    try {
      const res = await bulkAssignAgencyFiles({
        file_ids: Array.from(selectedIds),
        staff_id: bulkStaffId,
        instruction: bulkInstruction,
      });
      toast.success(res.data.message);
      setBulkAssignOpen(false);
      setBulkStaffId(''); setBulkInstruction('');
      setSelectedIds(new Set());
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk assign failed');
    } finally {
      setBulkAssigning(false);
    }
  };

  // Selection helpers (memoized)
  const assignableFiles = useMemo(() => files.filter(f => f.status === 'pending' || f.status === 'assigned'), [files]);
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === assignableFiles.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(assignableFiles.map(f => f.id)));
    }
  };
  const allSelected = assignableFiles.length > 0 && selectedIds.size === assignableFiles.length;

  // Memoized expensive calculations
  const selectedAgencyForComplete = useMemo(() =>
    completeDialog.file ? agencies.find(a => a.id === completeDialog.file.agency_id) : null,
    [completeDialog.file, agencies]
  );
  const cQty = parseInt(completeQty) || 0;
  const cRate = selectedAgencyForComplete?.rate || 0;
  const cFee = selectedAgencyForComplete?.convenience_fee || 0;
  const cTotal = cQty > 0 ? Math.round((cRate + cFee) * cQty * 100) / 100 : 0;

  const selectedFilesInfo = useMemo(() => {
    const selected = files.filter(f => selectedIds.has(f.id));
    return {
      files: selected,
      totalEntries: selected.reduce((s, f) => s + (f.entry_count || 0), 0),
    };
  }, [files, selectedIds]);

  const tableSummary = useMemo(() => ({
    totalFiles: files.length,
    totalEntries: files.reduce((s, f) => s + (f.entry_count || 0), 0),
    totalBookings: files.reduce((s, f) => s + (f.total_bookings_count || 0), 0),
    totalDebited: files.reduce((s, f) => s + (f.total_amount_debited || 0), 0),
  }), [files]);

  const StatusBadge = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded ${cfg.color}`}>
        <Icon size={14} weight="fill" /> {cfg.label}
      </span>
    );
  };

  if (!isAdmin) return <div className="p-4 text-red-600">Access denied.</div>;

  return (
    <div className="space-y-5" data-testid="agency-files-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900" data-testid="agency-files-title">Agency Files</h1>
          <p className="text-sm text-gray-500 mt-0.5">WhatsApp se aaye agency files manually enter aur track karo</p>
        </div>
        <Button onClick={() => setShowUpload(!showUpload)} className="bg-[#002FA7] hover:bg-[#0025880] gap-2 self-start sm:self-auto" data-testid="new-agency-file-btn">
          <PaperPlaneTilt size={18} /> New Agency File
        </Button>
      </div>

      {/* Stats Cards */}
      <AgencyFileStatsCards stats={stats} />

      {/* Upload Form */}
      {showUpload && (
        <div className="bg-white border-2 border-[#002FA7]/20 rounded-lg p-4 sm:p-5 space-y-4" data-testid="agency-file-upload-form">
          <h2 className="text-base font-semibold text-gray-900">New Agency File Entry</h2>

          {/* Row 1: Agency + Oil Company + Date */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-sm font-medium mb-1 block">Agency *</Label>
              <SearchableSelect
                options={agencies.map(a => ({ value: a.id, label: a.name, sublabel: `₹${a.rate}` }))}
                value={uploadAgencyId}
                onValueChange={setUploadAgencyId}
                placeholder="Agency select karo..."
                data-testid="upload-agency-select"
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-1 block">Oil Company</Label>
              <select value={uploadOilCompany} onChange={e => setUploadOilCompany(e.target.value)}
                className="w-full border border-gray-300 rounded-md h-10 px-3 text-sm" data-testid="upload-oil-company">
                {OIL_COMPANIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1 block">Entry Date *</Label>
              <Input type="date" value={uploadDate} onChange={e => setUploadDate(e.target.value)}
                className="text-sm" data-testid="upload-entry-date" />
            </div>
          </div>

          {/* Mode Toggle */}
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setUploadMode('text')}
              className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium border-2 transition-all ${
                uploadMode === 'text'
                  ? 'border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7]'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
              }`} data-testid="upload-mode-text">
              <PaperPlaneTilt size={16} /> Text Paste
            </button>
            <button type="button" onClick={() => setUploadMode('file')}
              className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium border-2 transition-all ${
                uploadMode === 'file'
                  ? 'border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7]'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
              }`} data-testid="upload-mode-file">
              <UploadSimple size={16} /> File Upload
            </button>
          </div>

          {/* Input Area */}
          {uploadMode === 'text' ? (
            <div>
              <Label className="text-sm font-medium mb-1 block">Mobile No. / LPG ID / Consumer ID</Label>
              <textarea value={uploadText} onChange={e => setUploadText(e.target.value)}
                placeholder="Ek line me ek entry likhein...&#10;9876543210&#10;9876543211&#10;LPG001234&#10;..."
                rows={6}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm resize-y focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none font-mono"
                data-testid="upload-text-content" />
              <p className="text-xs text-gray-400 mt-1">
                {uploadText.trim() ? (() => {
                  const lines = uploadText.trim().split('\n').filter(l => l.trim());
                  const unique = new Set(lines.map(l => l.trim().toUpperCase()));
                  const dupes = lines.length - unique.size;
                  return `${unique.size} unique entries${dupes > 0 ? ` (${dupes} duplicates will be auto-removed)` : ''}`;
                })() : 'No entries yet'}
              </p>
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-200 rounded-lg p-5 text-center bg-gray-50" data-testid="upload-file-drop">
              {uploadFile ? (
                <div className="space-y-2">
                  <FileText size={28} className="text-[#002FA7] mx-auto" />
                  <p className="text-sm font-medium text-gray-800">{uploadFile.name}</p>
                  <p className="text-xs text-gray-500">{(uploadFile.size / 1024).toFixed(1)} KB</p>
                  <Button variant="ghost" size="sm" onClick={() => setUploadFile(null)} className="text-red-500 hover:text-red-700 text-xs">
                    <Trash size={14} className="mr-1" /> Remove
                  </Button>
                </div>
              ) : (
                <>
                  <UploadSimple size={28} className="text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 mb-1">Excel / TXT / CSV Upload</p>
                  <p className="text-xs text-gray-400 mb-3">.xlsx, .csv, .txt (max 10MB)</p>
                  <div className="relative inline-block">
                    <input type="file" accept=".xlsx,.xls,.csv,.txt" onChange={e => setUploadFile(e.target.files[0])}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-testid="upload-file-input" />
                    <Button variant="outline" size="sm" className="gap-2"><UploadSimple size={16} /> Choose File</Button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Note + Submit */}
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            <div className="flex-1 w-full">
              <Label className="text-xs text-gray-500 mb-1 block">Note (Optional)</Label>
              <Input value={uploadNote} onChange={e => setUploadNote(e.target.value)} placeholder="e.g. April WhatsApp file"
                className="text-sm" data-testid="upload-note" />
            </div>
            {uploadMode === 'text' ? (
              <Button onClick={handleSubmitText} disabled={uploading || !uploadAgencyId || !uploadText.trim()}
                className="bg-[#002FA7] hover:bg-[#002590] gap-2 w-full sm:w-auto" data-testid="submit-text-btn">
                <PaperPlaneTilt size={16} /> {uploading ? 'Submitting...' : 'Submit'}
              </Button>
            ) : (
              <Button onClick={handleUploadFile} disabled={uploading || !uploadAgencyId || !uploadFile}
                className="bg-[#002FA7] hover:bg-[#002590] gap-2 w-full sm:w-auto" data-testid="submit-file-btn">
                <UploadSimple size={16} /> {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Bulk Assign Bar */}
      {selectedIds.size > 0 && (
        <div className="bg-[#002FA7] text-white rounded-lg px-4 py-3 flex items-center justify-between gap-3 animate-in slide-in-from-top-2" data-testid="bulk-assign-bar">
          <div className="flex items-center gap-3">
            <input type="checkbox" checked={allSelected} onChange={toggleSelectAll}
              className="w-4 h-4 rounded accent-white" />
            <span className="text-sm font-medium">{selectedIds.size} file{selectedIds.size > 1 ? 's' : ''} selected</span>
            <span className="text-xs text-blue-200">
              ({selectedFilesInfo.totalEntries} total entries)
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" className="bg-white text-[#002FA7] hover:bg-blue-50 text-xs h-8 px-3 font-semibold"
              onClick={() => { setBulkAssignOpen(true); setBulkStaffId(''); setBulkInstruction(''); }}
              data-testid="bulk-assign-btn">
              <UserPlus size={16} className="mr-1.5" /> Bulk Assign ({selectedIds.size})
            </Button>
            <Button size="sm" variant="ghost" className="text-white/70 hover:text-white hover:bg-white/10 text-xs h-8 px-2"
              onClick={() => setSelectedIds(new Set())} data-testid="clear-selection-btn">
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 bg-white border border-gray-200 rounded-lg p-3" data-testid="agency-files-filters">
        <Funnel size={16} className="text-gray-400" />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="border border-gray-200 rounded-md h-8 px-2 text-xs" data-testid="filter-status">
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="assigned">Assigned</option>
          <option value="completed">Completed</option>
        </select>
        <select value={agencyFilter} onChange={e => setAgencyFilter(e.target.value)}
          className="border border-gray-200 rounded-md h-8 px-2 text-xs max-w-[160px]" data-testid="filter-agency">
          <option value="">All Agencies</option>
          {agencies.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <select value={staffFilter} onChange={e => setStaffFilter(e.target.value)}
          className="border border-gray-200 rounded-md h-8 px-2 text-xs max-w-[140px]" data-testid="filter-staff">
          <option value="">All Staff</option>
          {staffList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <div className="flex items-center gap-1">
          <CalendarBlank size={14} className="text-gray-400" />
          <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
            className="h-8 text-xs w-[120px] px-1" data-testid="filter-start-date" />
          <span className="text-gray-400 text-xs">to</span>
          <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
            className="h-8 text-xs w-[120px] px-1" data-testid="filter-end-date" />
        </div>
        {(statusFilter !== 'all' || agencyFilter || startDate || endDate || staffFilter) && (
          <Button variant="ghost" size="sm" className="text-xs h-7 text-gray-500"
            onClick={() => { setStatusFilter('all'); setAgencyFilter(''); setStartDate(''); setEndDate(''); setStaffFilter(''); }}
            data-testid="clear-filters-btn">
            Clear
          </Button>
        )}
      </div>

      {/* Files Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="agency-files-table">
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : files.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Files size={40} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">Koi agency file nahi mili</p>
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="sm:hidden divide-y divide-gray-100">
              {files.map(f => (
                <div key={f.id} className={`p-3 space-y-2 ${selectedIds.has(f.id) ? 'bg-blue-50/60' : ''}`} data-testid={`af-card-${f.id}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex items-start gap-2">
                      {(f.status === 'pending' || f.status === 'assigned') && (
                        <input type="checkbox" checked={selectedIds.has(f.id)} onChange={() => toggleSelect(f.id)}
                          className="w-4 h-4 rounded accent-[#002FA7] mt-0.5" />
                      )}
                      <div>
                        <p className="font-medium text-sm text-gray-900">{f.agency_name}
                          {f.agency_rate > 0 && (
                            <span className="ml-2 text-[10px] text-emerald-700 font-bold bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                              💰 ₹{f.agency_rate}/cyl
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">{f.entry_date} &middot; {f.entry_count} unique entries
                          {f.duplicate_count > 0 && <span className="text-orange-500"> ({f.duplicate_count} dupes removed)</span>}
                        </p>
                      </div>
                    </div>
                    <StatusBadge status={f.status} />
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="bg-gray-100 px-1.5 py-0.5 rounded">{f.entry_mode === 'text' ? 'Text' : 'File'}</span>
                    <span>{f.oil_company}</span>
                    {f.assigned_to_name && <span>&#8594; {f.assigned_to_name}</span>}
                  </div>
                  {f.total_amount_debited > 0 && (
                    <p className="text-sm font-semibold text-red-600">{fmt(f.total_amount_debited)}</p>
                  )}
                  <div className="flex gap-1.5">
                    {f.status === 'pending' && (
                      <>
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-xs h-7 px-2"
                          onClick={() => { setAssignDialog({ open: true, file: f }); setAssignStaffId(''); setAssignInstruction(''); }}>
                          <UserPlus size={14} className="mr-1" /> Assign
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500 text-xs h-7 px-2" onClick={() => handleDelete(f.id)}>
                          <Trash size={14} />
                        </Button>
                      </>
                    )}
                    {(f.status === 'assigned' || f.status === 'pending') && (
                      <Button size="sm" className="bg-green-600 hover:bg-green-700 text-xs h-7 px-2"
                        onClick={() => { setCompleteDialog({ open: true, file: f }); setCompleteQty(String(f.entry_count || '')); setCompleteSource('cash'); }}>
                        <CheckCircle size={14} className="mr-1" /> Complete
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <Table className="table-dense">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <input type="checkbox" checked={allSelected} onChange={toggleSelectAll}
                        className="w-3.5 h-3.5 rounded accent-[#002FA7]" data-testid="select-all-checkbox" />
                    </TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Agency</TableHead>
                    <TableHead>Oil Co.</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead className="text-right">Entries</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead>Created By</TableHead>
                    <TableHead className="text-right">Bookings</TableHead>
                    <TableHead className="text-right">Debited</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {files.map(f => (
                    <TableRow key={f.id} data-testid={`af-row-${f.id}`}
                      className={selectedIds.has(f.id) ? 'bg-blue-50/60' : ''}>
                      <TableCell>
                        {(f.status === 'pending' || f.status === 'assigned') ? (
                          <input type="checkbox" checked={selectedIds.has(f.id)} onChange={() => toggleSelect(f.id)}
                            className="w-3.5 h-3.5 rounded accent-[#002FA7]" data-testid={`select-af-${f.id}`} />
                        ) : <span className="w-3.5 h-3.5 inline-block" />}
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap">{f.entry_date}</TableCell>
                      <TableCell className="font-medium text-sm">
                        {f.agency_name}
                        {f.agency_rate > 0 && (
                          <div className="text-[10px] text-emerald-700 font-bold mt-0.5">💰 ₹{f.agency_rate}/cyl</div>
                        )}
                      </TableCell>
                      <TableCell><span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{f.oil_company}</span></TableCell>
                      <TableCell>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${f.entry_mode === 'text' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                          {f.entry_mode === 'text' ? 'Text' : 'File'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {f.entry_count}
                        {f.duplicate_count > 0 && (
                          <span className="text-xs text-orange-500 ml-1" title={`${f.duplicate_count} duplicates removed`}>(-{f.duplicate_count})</span>
                        )}
                      </TableCell>
                      <TableCell><StatusBadge status={f.status} /></TableCell>
                      <TableCell className="text-sm">{f.assigned_to_name || <span className="text-gray-400">-</span>}</TableCell>
                      <TableCell className="text-sm text-gray-500">{f.created_by_name}</TableCell>
                      <TableCell className="text-right text-sm font-medium">{f.total_bookings_count || '-'}</TableCell>
                      <TableCell className="text-right text-sm font-medium">
                        {f.total_amount_debited > 0 ? <span className="text-red-600">{fmt(f.total_amount_debited)}</span> : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          {f.url && (
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-blue-600"
                              onClick={() => downloadFileWithName(
                                `${window.location.origin}/api/agency-files/${f.id}/download`,
                                f.url, f.original_filename
                              )} data-testid={`download-af-${f.id}`}>
                              <DownloadSimple size={15} />
                            </Button>
                          )}
                          {f.status === 'pending' && (
                            <>
                              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-xs h-7 px-2"
                                onClick={() => { setAssignDialog({ open: true, file: f }); setAssignStaffId(''); setAssignInstruction(''); }}
                                data-testid={`assign-af-${f.id}`}>
                                <UserPlus size={14} className="mr-1" /> Assign
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                                onClick={() => handleDelete(f.id)} data-testid={`delete-af-${f.id}`}>
                                <Trash size={15} />
                              </Button>
                            </>
                          )}
                          {(f.status === 'assigned' || f.status === 'pending') && (
                            <Button size="sm" className="bg-green-600 hover:bg-green-700 text-xs h-7 px-2"
                              onClick={() => { setCompleteDialog({ open: true, file: f }); setCompleteQty(String(f.entry_count || '')); setCompleteSource('cash'); }}
                              data-testid={`complete-af-${f.id}`}>
                              <CheckCircle size={14} className="mr-1" /> Complete
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Summary Row */}
            <div className="border-t border-gray-200 px-4 py-2.5 bg-gray-50 flex flex-wrap items-center gap-4 text-xs text-gray-600" data-testid="af-summary">
              <span>Total: <strong>{tableSummary.totalFiles}</strong> files</span>
              <span>Entries: <strong>{tableSummary.totalEntries}</strong></span>
              <span>Bookings: <strong>{tableSummary.totalBookings}</strong></span>
              <span>Debited: <strong className="text-red-600">{fmt(tableSummary.totalDebited)}</strong></span>
            </div>
          </>
        )}
      </div>

      {/* Assign Dialog */}
      <AgencyFileAssignDialog
        assignDialog={assignDialog}
        setAssignDialog={setAssignDialog}
        staffList={staffList}
        assignStaffId={assignStaffId}
        setAssignStaffId={setAssignStaffId}
        assignInstruction={assignInstruction}
        setAssignInstruction={setAssignInstruction}
        handleAssign={handleAssign}
      />

      {/* Complete Dialog */}
      <AgencyFileCompleteDialog
        completeDialog={completeDialog}
        setCompleteDialog={setCompleteDialog}
        selectedAgencyForComplete={selectedAgencyForComplete}
        cRate={cRate}
        cFee={cFee}
        cQty={cQty}
        cTotal={cTotal}
        completeQty={completeQty}
        setCompleteQty={setCompleteQty}
        completeSource={completeSource}
        setCompleteSource={setCompleteSource}
        completing={completing}
        handleComplete={handleComplete}
      />

      {/* Bulk Assign Dialog */}
      <AgencyFileBulkAssignDialog
        bulkAssignOpen={bulkAssignOpen}
        setBulkAssignOpen={setBulkAssignOpen}
        selectedIds={selectedIds}
        selectedFilesInfo={selectedFilesInfo}
        staffList={staffList}
        bulkStaffId={bulkStaffId}
        setBulkStaffId={setBulkStaffId}
        bulkInstruction={bulkInstruction}
        setBulkInstruction={setBulkInstruction}
        bulkAssigning={bulkAssigning}
        handleBulkAssign={handleBulkAssign}
      />
    </div>
  );
}
